import {
  App, Form, UploadFile
} from "antd";
import type { GetProp, UploadProps } from "antd";
import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { UPDATE_PATIENT, UPDATE_PROFESSIONIST } from "../settings.gql";
// import { PREFIXES } from "../../../../constants/app";

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const getBase64 = (img: FileType, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const useProfile = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { data, isDoctor, refetchUser } = useCurrentUser();
  const [form] = Form.useForm();
  const [previewImage, setPreviewImage] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [update, { loading }] = useMutation(isDoctor ? UPDATE_PROFESSIONIST : UPDATE_PATIENT, {
    fetchPolicy: "no-cache",
    onCompleted: () => {
      refetchUser();
      message.success(t("user.success.updateUser"));
    }
  });
  const [isAvatarSizeError, setIsAvatarSizeError] = useState(false);

  const beforeUpload = (file: FileType) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error(t("form.error.avatarImgFormat"));
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t("form.error.avatarImgSize"));
    }
    setIsAvatarSizeError(!isLt2M);
    return isJpgOrPng && isLt2M;
  };

  useEffect(() => {
    if (data) {
      const {
        name,
        surname,
        studies,
        bio,
        phone,
        photo
      } = data;

      // const phoneArray = (phoneNumber: string) => {
      //   const validPrefix = PREFIXES
      //     .find(prefix => phoneNumber.includes(prefix.label));
      //
      //   return validPrefix
      //     ? [validPrefix.label, phoneNumber.substring(validPrefix.label.length)] : [];
      // };

      if (photo) {
        setFileList([
          {
            uid: "-4",
            name: "image.png",
            status: "done",
            url: photo,
          },
        ]);
      }

      form.setFieldsValue({
        name,
        surname,
        studies,
        bio,
        phone: {
          // prefix: phone && phoneArray(phone).length ? phoneArray(phone)[0] : PREFIXES[0].value,
          // number: phone && phoneArray(phone) ? phoneArray(phone)[1] : null
          number: phone
        }
      });
    }
  }, [form, data]);

  const onFileChange: UploadProps["onChange"] = (props) => {
    setFileList(props.file.status === "removed" ? [] : [props.file]);
  };

  const onSubmit = (values: any) => {
    if (isDoctor) {
      const photoObj = values.photo?.file?.originFileObj ?? values.photo?.file;

      if (isAvatarSizeError) {
        message.error(t("form.error.avatarImgSize"));
        return;
      }

      update({
        variables: {
          professionist_id: Number(data.id),
          photo: photoObj?.status === "removed" ? "" : photoObj,
          is_photo_deleted: photoObj && photoObj.status === "removed",
          studies: values.studies,
          name: values.name,
          surname: values.surname,
          bio: values.bio,
          phone: values.phone?.number ? `${values.phone.number}` : null
        }
      });
    } else {
      update({
        variables: {
          name: values.name,
          surname: values.surname,
          bio: values.bio ?? "",
          phone: values.phone?.number ? `${values.phone.number}` : null
        }
      });
    }
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      // @ts-ignore
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  return {
    form,
    loading,
    onFileChange,
    onSubmit,
    beforeUpload,
    handlePreview,
    previewOpen,
    setPreviewOpen,
    previewImage,
    setPreviewImage,
    fileList
  };
};

export default useProfile;
